import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { appointmentsApi, clientsApi, departmentsApi, extractList } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const AppointmentCreatePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [client_id, setClientId] = useState<number | ''>('');
  const [department_id, setDepartmentId] = useState<number | ''>('');
  const [type, setType] = useState<'new' | 'exists'>('new');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    const state = (location.state ?? {}) as any;
    if (state?.date && typeof state.date === 'string') setDate(state.date);
    if (state?.time && typeof state.time === 'string') setTime(state.time);
    else if (state?.date && typeof state.date === 'string' && !time) setTime('09:00');
  }, []);

  const { data: clientsRes } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list({}) });
  const clients = extractList(clientsRes);

  const { data: deptsRes } = useQuery({ queryKey: ['departments'], queryFn: () => departmentsApi.list() });
  const departments = extractList(deptsRes);

  const create = useMutation({
    mutationFn: () =>
      appointmentsApi.create({
        client_id: client_id as number,
        department_id: department_id === '' ? null : department_id,
        type,
        date,
        time,
        name,
        mobile: mobile || null,
        note: note || null,
      }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      const id = data?.data?.id ?? (data?.data && (data.data as any).id);
      if (id) navigate(`/dashboard/appointments/${id}`);
      else navigate('/dashboard/appointments');
      toast.success('Appointment created.');
    },
    onError: (e: any) => toast.error(e?.message || 'Create failed.'),
  });

  const canSubmit = typeof client_id === 'number' && date.trim() && time.trim() && name.trim();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate();
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">New Appointment</h1>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Client" hint="Required">
          <select className={inputClass} value={client_id === '' ? '' : client_id} onChange={(e) => setClientId(e.target.value === '' ? '' : Number(e.target.value))} required>
            <option value="">Select client</option>
            {clients.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormField>
        <FormField label="Department">
          <select className={inputClass} value={department_id === '' ? '' : department_id} onChange={(e) => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">— None —</option>
            {departments.map((d: any) => <option key={d.id} value={d.id}>{d.department_name}</option>)}
          </select>
        </FormField>
        <FormField label="Type" hint="Required">
          <select className={inputClass} value={type} onChange={(e) => setType(e.target.value as 'new' | 'exists')} required>
            <option value="new">New</option>
            <option value="exists">Existing</option>
          </select>
        </FormField>
        <FormField label="Name">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
        </FormField>
        <FormField label="Date">
          <input className={inputClass} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </FormField>
        <FormField label="Time">
          <input className={inputClass} type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
        </FormField>
        <FormField label="Mobile">
          <input className={inputClass} value={mobile} onChange={(e) => setMobile(e.target.value)} />
        </FormField>
        <div className="md:col-span-2">
          <FormField label="Note">
            <textarea className={inputClass} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </FormField>
        </div>
        <div className="md:col-span-2 flex gap-2 mt-2">
          <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!canSubmit || create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
};

export default AppointmentCreatePage;
